import { Button, Grid, makeStyles, MobileStepper, Paper, TextField, Typography, useTheme } from '@material-ui/core';
import { green, red } from '@material-ui/core/colors';
import { AttachMoney, CreditCard, KeyboardArrowLeft, KeyboardArrowRight, Timer } from '@material-ui/icons';
import React, { useContext, useRef, useState } from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import AppBarContext from '../../contexts/AppBarContext';
import EmployeeInfoContext from '../../contexts/EmployeeInfoContext';
import useLoadContext from '../../hooks/useLoadContext';
import { getSource } from '../../utils/AccountsAPI';
import { addDays, getUTCDateString, toastError } from '../../utils/Functions';
import { formatCurrency, getWeekEndingDate } from '../../utils/TSFunctions';
import { CreditCardTransaction, getUnassignedCardTransactionsForUser } from '../creditCards/CreditCardAPI';
import Tile from './DashboardComponents/Tile';
import TileGroup from './DashboardComponents/TileGroup';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import { useStateRef } from '../../hooks/useStateRef';
import { Broadcast, deleteBroadcast, getBroadcasts, putBroadcast } from './BroadcastAPI';
import { cloneDeep } from 'lodash';
import { useToast } from '../../hooks/useToast';
import { unstable_batchedUpdates } from 'react-dom';
import { useConfirmation } from '../../hooks/useConfirmation';
import DraftEditor from './DashboardComponents/DraftEditor';
import ExpenseReport from '../expenses/ExpenseReport';
import Timesheet from '../timesheets/Timesheet';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  editor: {
    minHeight: '40vh',
  },
  editorHeader: {
    display: 'grid',
    width: '100%',
    minHeight: theme.spacing(3),
    lineHeight: theme.spacing(3),
    fontSize: theme.spacing(3),
    backgroundColor: 'rgba(0,0,0,.5)',
    gridTemplateColumns: 'repeat(12,1fr)',
  },
  broadcastContainer: {
    margin: 'auto',
  },
  editorHeaderIcon: {
    height: theme.spacing(2) + 'px !important',
    width: theme.spacing(2) + 'px !important',
    padding: theme.spacing(0.5),
    margin: 'auto',
  },
  editorHeaderIconButton: {
    borderRadius: 0,
  },
  broadcastTitleText: {
    margin: 'auto',
    width: '100%',
    gridColumn: '2 / span 2',
  },
  broadcastTitle: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    padding: theme.spacing(1),
  },
  broadcastTitleTextField: {
    gridColumn: '2 / span 2',
    margin: theme.spacing(1) + 'px auto',
  },
}));

export default function Dashboard(props) {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const toast = useToast();
  const confirm = useConfirmation();
  const [employeeInfo] = useContext(EmployeeInfoContext);
  useLoadContext(AppBarContext, { title: 'Dashboard' });

  const draftRef = useRef(null);

  const [ccTrx, setCCTrx] = useState<CreditCardTransaction[] | undefined>();
  const [expenses, setExpenses] = useState<ExpenseReport | undefined>();
  const [timesheet, setTimesheet] = useState<Timesheet | undefined>();
  const maxBroadcasts = 5;
  const [broadcasts, setBroadcasts, broadcastsRef] = useStateRef<Broadcast[]>([]);
  const [activeBroadcast, setActiveBroadcast] = useState<number>(0);
  const [editorState, setEditorState, editorStateRef] = useStateRef<EditorState>(EditorState.createEmpty());
  const [saving, setSaving] = useState<boolean>(false);
  const [savingTitle, setSavingTitle] = useState<boolean>(false);
  const [weekEnding, setWeekEnding] = useState<Date>(getWeekEndingDate(addDays(new Date(), employeeInfo?.hasClaim('defaultPreviousWeekEnding', 'true') ? -7 : 0)));

  useEffect(() => {
    var source = getSource();
    if (employeeInfo && (employeeInfo?.creditCards?.length ?? 0) > 0)
      getUnassignedCardTransactionsForUser(employeeInfo.id, source.token)
        .then((trx) => setCCTrx(trx))
        .catch((err) => toastError(toast, err));
    return () => source.cancel();
  }, [employeeInfo, toast]);

  useEffect(() => {
    var source = getSource();
    if (employeeInfo)
      getBroadcasts(maxBroadcasts, employeeInfo?.isInRole('Admin'), source.token)
        .then((b) => {
          unstable_batchedUpdates(() => {
            setBroadcasts(b ?? []);
            if (b.length > 0) {
              var contentState;
              try {
                contentState = convertFromRaw(JSON.parse(b?.[b.length - 1]?.message ?? ''));
              } catch {
                contentState = null;
              }
              setEditorState(contentState ? EditorState.createWithContent(contentState) : EditorState.createEmpty());
              setActiveBroadcast(b.length - 1);
            }
          });
        })
        .catch((err) => toastError(toast, err));
    return () => source.cancel();
  }, [setEditorState, employeeInfo, setBroadcasts, toast]);

  useEffect(() => {
    var source = getSource();
    if (employeeInfo) {
      if (!employeeInfo.hasClaim('DisableTimesheet', 'true')) {
        let timesheet = new Timesheet(employeeInfo, weekEnding);
        timesheet
          .getAPITimesheet(source.token)
          .then((ts) => setTimesheet(ts))
          .catch((err) => toastError(toast, err));
      }

      if (!employeeInfo.hasClaim('DisableExpenses', 'true')) {
        let report = new ExpenseReport(employeeInfo, weekEnding);
        report
          .getAPIExpenseReport(source.token)
          .then((r) => setExpenses(r))
          .catch((err) => toastError(toast, err));
      }
    }
    return () => source.cancel();
  }, [setEditorState, employeeInfo, setBroadcasts, toast, weekEnding]);

  useEffect(() => {
    var newWE = getWeekEndingDate(addDays(new Date(), employeeInfo?.hasClaim('defaultPreviousWeekEnding', 'true') ? -7 : 0));
    if (weekEnding.getTime() !== newWE.getTime()) setWeekEnding(newWE);
  }, [employeeInfo, weekEnding]);

  const handleNext = () => {
    unstable_batchedUpdates(() => {
      if (activeBroadcast + 1 < broadcasts.length) {
        var contentState = convertFromRaw(JSON.parse(broadcasts?.[activeBroadcast + 1]?.message ?? ''));
        setEditorState(EditorState.createWithContent(contentState));
      } else {
        setEditorState(EditorState.createEmpty());
      }
      setActiveBroadcast((n) => n + 1);
    });
  };

  const handleBack = () => {
    unstable_batchedUpdates(() => {
      var contentState = convertFromRaw(JSON.parse(broadcasts?.[activeBroadcast - 1]?.message ?? ''));
      setEditorState(EditorState.createWithContent(contentState));
      setActiveBroadcast((n) => n - 1);
    });
  };

  const onDelete = () => {
    confirm('Are you sure you want to delete this broadcast?').then(() => {
      setSaving(true);
      var promise: Promise<void>;
      if (broadcasts[activeBroadcast]?.id)
        promise = deleteBroadcast(broadcasts[activeBroadcast]?.id).catch((err) => {
          setSaving(false);
          toastError(err, toast);
        });
      else promise = Promise.resolve();

      promise.then(() => {
        unstable_batchedUpdates(() => {
          var clone = cloneDeep(broadcasts);
          clone.splice(activeBroadcast, 1);
          setBroadcasts(clone);

          var newActiveBroadcast = clone.length > activeBroadcast ? activeBroadcast : activeBroadcast >= 1 ? activeBroadcast - 1 : 0;

          if (clone.length > 0) setEditorState(EditorState.createWithContent(convertFromRaw(JSON.parse(clone?.[newActiveBroadcast]?.message ?? ''))));
          else setEditorState(EditorState.createEmpty());

          setActiveBroadcast(newActiveBroadcast);
          setSaving(false);
        });
      });
    });
  };

  const onEditorChange = (newEditor: EditorState) => {
    if (newEditor.getLastChangeType() && newEditor.getCurrentContent() !== editorStateRef.current.getCurrentContent()) {
      setTimeout(() => {
        //if (newEditor === editorStateRef.current) {
        if (newEditor.getCurrentContent() === editorStateRef.current.getCurrentContent()) {
          var broadcast = broadcastsRef?.current?.[activeBroadcast] ?? {};
          broadcast.message = JSON.stringify(convertToRaw(newEditor.getCurrentContent()));
          putBroadcast(broadcast)
            .then((b) => {
              var clone = cloneDeep(broadcasts);
              var index = clone.findIndex((broad) => broad.id === b.id);
              if (index > -1) clone.splice(index, 1, b);
              else clone.push(b);
              setBroadcasts(clone);
              setSaving(false);
            })
            .catch((err) => {
              setSaving(false);
              toastError(toast, err);
            });
        }
      }, 1000);

      setSaving(true);
    }
    setEditorState(newEditor);
  };

  const onBroadcastTitleBlur = (e) => {
    var original = broadcasts;
    var clone = cloneDeep(broadcasts);
    var broadcast = clone[activeBroadcast];
    if (!broadcast) {
      const newBroadcast = { title: e.target.value, message: JSON.stringify(convertToRaw(editorStateRef.current.getCurrentContent())) } as Broadcast;
      clone.splice(activeBroadcast, 0, newBroadcast);
    } else broadcast.title = e.target.value;
    setBroadcasts(clone);

    setTimeout(() => {
      if (clone[activeBroadcast]?.title === broadcastsRef.current?.[activeBroadcast]?.title)
        putBroadcast(clone[activeBroadcast])
          .then((b) => {
            var clone = cloneDeep(broadcasts);
            var index = clone.findIndex((broad) => broad.id === b.id);
            if (index > -1) clone.splice(index, 1, b);
            else clone.push(b);
            setBroadcasts(clone);
            setSavingTitle(false);
          })
          .catch((err) => {
            setBroadcasts(original);
            setSavingTitle(false);
            toastError(toast, err);
          });
    }, 2000);

    setSavingTitle(true);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <Paper square className={classes.broadcastContainer} elevation={3}>
            <Paper square className={classes.broadcastTitle}>
              {employeeInfo?.isInRole('Admin') ? (
                <TextField
                  key={broadcasts?.[activeBroadcast]?.title ?? ''}
                  size='small'
                  className={classes.broadcastTitleTextField}
                  label='Title'
                  defaultValue={broadcasts?.[activeBroadcast]?.title ?? ''}
                  onBlur={onBroadcastTitleBlur}
                  fullWidth
                  disabled={saving}
                />
              ) : (
                <Typography variant='h6' className={classes.broadcastTitleText}>
                  {broadcasts?.[activeBroadcast]?.title ?? '\xa0'}
                </Typography>
              )}
            </Paper>
            <DraftEditor
              draftRef={draftRef}
              editorState={editorState}
              onEditorChange={onEditorChange}
              onDelete={onDelete}
              saving={saving || savingTitle}
              readOnly={savingTitle}
            />
            <MobileStepper
              steps={broadcasts.length}
              position='static'
              variant='text'
              activeStep={activeBroadcast}
              nextButton={
                <Button size='small' onClick={handleNext} disabled={saving || activeBroadcast === broadcasts.length + (employeeInfo?.isInRole('Admin') ? 0 : -1)}>
                  Next
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button size='small' onClick={handleBack} disabled={saving || activeBroadcast === 0}>
                  <KeyboardArrowLeft />
                  Back
                </Button>
              }
            />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <TileGroup title={`Week of ${getUTCDateString(weekEnding, 'mm/dd')}`} spacing={2}>
            {employeeInfo && !employeeInfo?.hasClaim('DisableTimesheet', 'true') ? (
              <Grid item xs={12} sm={6}>
                <Tile
                  title={'Hours'}
                  value={timesheet?.getTotalHours() ?? 0}
                  Icon={Timer}
                  color={theme.palette.primary.main}
                  actionText='Go To Timesheets'
                  actionOnClick={() => history.push('/Timesheet')}
                  ActionIcon
                  loading={!timesheet}
                />
              </Grid>
            ) : null}
            {employeeInfo && !employeeInfo?.hasClaim('DisableExpenses', 'true') ? (
              <Grid item xs={12} sm={6}>
                <Tile
                  title={'Expenses'}
                  value={formatCurrency(expenses?.getTotalExpenses() ?? 0)}
                  Icon={AttachMoney}
                  color={green[700]}
                  actionText='Go To Expenses'
                  actionOnClick={() => history.push('/Expenses')}
                  ActionIcon
                  loading={!expenses}
                />
              </Grid>
            ) : null}
            {(employeeInfo?.creditCards?.length ?? 0) > 0 ? (
              <Grid item xs={12} sm={6}>
                <Tile
                  title={'Unassigned Transactions'}
                  value={ccTrx?.reduce((sum, trx) => sum + trx.assignments.filter((a) => a.userId === employeeInfo?.id).length, 0)}
                  Icon={CreditCard}
                  color={red[900]}
                  actionText='Go To Credit Card Dashboard'
                  actionOnClick={() => history.push('/CreditCards')}
                  ActionIcon
                  loading={!ccTrx}
                />
              </Grid>
            ) : null}
          </TileGroup>
        </Grid>
      </Grid>
    </div>
  );
}
